import React from 'react';
import './NewHome.css';
import GithubActivity from './GithubActivity';
import Architecture from './Architecture';

// Expertise grouped by engineering domain.
const expertise = [
    {
        domain: 'Languages',
        items: ['Go', 'TypeScript', 'Node.js', 'JavaScript'],
    },
    {
        domain: 'Cloud & Infrastructure',
        items: ['GCP', 'Kubernetes', 'Terraform', 'Helm', 'ArgoCD', 'Docker'],
    },
    {
        domain: 'Data & Messaging',
        items: ['PostgreSQL', 'Pub/Sub'],
    },
];

const impact = [
    {
        metric: '~4x',
        label: 'Throughput improvement',
        detail: 'Modernised a backend service for roughly 4x higher throughput while substantially cutting per-pod memory.',
    },
    {
        metric: '~25x',
        label: 'Memory reduction per pod',
        detail: 'Same modernisation effort cut per-pod memory footprint by roughly 25x, improving density and cost.',
    },
    {
        metric: '<12 ms',
        label: 'Backend latency',
        detail: 'Reduced backend latency from ~65 ms to under 12 ms on a production service.',
    },
];

const projects = [
    // Professional accomplishments live under Experience, not here.
    // Add personal / open-source projects only. Section hidden while empty.
];

const experience = [
    {
        role: 'Senior Software Developer',
        org: 'SAP Emarsys',
        period: 'Apr 2026 – Present',
        note: 'Build and operate cloud-native email delivery infrastructure on GCP and Kubernetes. Work spans distributed-systems modernisation and infrastructure automation (Terraform, Helm, ArgoCD). Contributed to a service modernisation that improved throughput ~4x and reduced per-pod memory footprint by roughly 25x.',
    },
    {
        role: 'Software Developer',
        org: 'SAP Emarsys',
        period: 'Jan 2024 – Apr 2026',
        note: 'Joined Emarsys working on distributed backend services for large-scale email delivery, contributing to reliability and performance improvements across the platform.',
    },
    {
        role: 'Software Developer',
        org: 'Deutsche Telekom',
        note: 'Backend engineering on production services. Reduced backend latency on one service from ~65 ms to under 12 ms.',
    },
    {
        role: 'Software Developer',
        org: 'DMG MORI / HEITEC',
        note: 'Backend and systems engineering.',
    },
];

const NewHome = () => {
    return (
        <>
            <a href="#main" className="skip-link">Skip to content</a>

            <header className="nav">
                <div className="container nav-inner">
                    <a href="#top" className="nav-logo" aria-label="Kornel Kovats — home">KK</a>
                    <nav aria-label="Primary">
                        <ul className="nav-links">
                            <li><a href="#about">About</a></li>
                            <li><a href="#impact">Impact</a></li>
                            <li><a href="#expertise">Expertise</a></li>
                            <li><a href="#architecture">Architecture</a></li>
                            <li><a href="#experience">Experience</a></li>
                            <li><a href="#github">GitHub</a></li>
                            <li><a href="#contact">Contact</a></li>
                        </ul>
                    </nav>
                </div>
            </header>

            <main id="main">
                {/* Hero */}
                <section id="top" className="hero">
                    <div className="container">
                        <p className="eyebrow">Senior Software Engineer · Cloud Infrastructure &amp; Distributed Systems</p>
                        <h1 className="hero-title">Kornel Kovats</h1>
                        <p className="hero-lede">
                            I design, build, and operate cloud-native distributed systems
                            on GCP and Kubernetes — focused on reliability, scalability,
                            and performance in production.
                        </p>
                        <div className="hero-actions">
                            <a href="#contact" className="btn btn-primary">Get in touch</a>
                            <a href="/2025_KKP.pdf" download className="btn btn-ghost">Download CV</a>
                        </div>
                    </div>
                </section>

                {/* About */}
                <section id="about" className="section">
                    <div className="container">
                        <p className="eyebrow">01 — About</p>
                        <h2 className="section-title">What I do</h2>
                        <p className="hero-lede">
                            I'm a senior software engineer specialising in cloud
                            infrastructure and distributed systems. Most of my work is
                            backend and platform engineering: building services that
                            stay fast and dependable under production load, and the
                            automation that keeps them running.
                        </p>
                        <p className="hero-lede">
                            Day to day I work across GCP, Kubernetes, and Go, with
                            infrastructure as code through Terraform, Helm, and ArgoCD.
                            I care most about the parts that are hard to see from the
                            outside — reliability, scalability, and squeezing latency
                            and resource use out of systems that are already live.
                        </p>
                    </div>
                </section>

                {/* Selected impact */}
                <section id="impact" className="section">
                    <div className="container">
                        <p className="eyebrow">02 — Selected impact</p>
                        <h2 className="section-title">Engineering outcomes</h2>
                        <ul className="impact-grid">
                            {impact.map((it, i) => (
                                <li key={i} className="impact-card">
                                    <p className="impact-metric">{it.metric}</p>
                                    <p className="impact-label">{it.label}</p>
                                    <p className="impact-detail">{it.detail}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Projects — hidden while `projects` is empty */}
                {projects.length > 0 && (
                <section id="projects" className="section">
                    <div className="container">
                        <p className="eyebrow">03 — Projects</p>
                        <h2 className="section-title">Selected work</h2>
                        <div className="project-list">
                            {projects.map((p) => (
                                <article key={p.name} className="project">
                                    <div className="project-head">
                                        <h3>{p.name}</h3>
                                        <ul className="tag-row">
                                            {p.stack.map((s) => (
                                                <li key={s} className="tag">{s}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <p className="project-summary">{p.summary}</p>
                                    <a href={p.href} target="_blank" rel="noreferrer" className="project-link">
                                        {p.label} <span aria-hidden="true">→</span>
                                    </a>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
                )}

                {/* Expertise */}
                <section id="expertise" className="section">
                    <div className="container">
                        <p className="eyebrow">03 — Expertise</p>
                        <h2 className="section-title">By domain</h2>
                        <div className="expertise-grid">
                            {expertise.map((group) => (
                                <div key={group.domain} className="expertise-group">
                                    <h3 className="expertise-domain">{group.domain}</h3>
                                    <ul className="expertise-items">
                                        {group.items.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Architecture */}
                <Architecture />

                {/* Experience */}
                <section id="experience" className="section">
                    <div className="container">
                        <p className="eyebrow">05 — Experience</p>
                        <h2 className="section-title">Experience</h2>
                        <ol className="timeline">
                            {experience.map((e, i) => (
                                <li key={i} className="timeline-item">
                                    <p className="timeline-period">{e.period}</p>
                                    <div>
                                        <h3 className="timeline-role">{e.role}</h3>
                                        <p className="timeline-org">{e.org}</p>
                                        <p className="timeline-note">{e.note}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* GitHub activity — below Experience */}
                <GithubActivity />

                {/* Contact */}
                <section id="contact" className="section">
                    <div className="container contact-inner">
                        <div>
                            <p className="eyebrow">07 — Contact</p>
                            <h2 className="section-title">Get in touch</h2>
                            <p className="contact-lede">
                                Open to conversations about cloud infrastructure and
                                distributed-systems engineering.
                            </p>
                        </div>
                        <form
                            className="contact-form"
                            action="https://formspree.io/f/mnpjkvwy"
                            method="POST"
                        >
                            <label className="field">
                                <span>Name</span>
                                <input type="text" name="name" required />
                            </label>
                            <label className="field">
                                <span>Email</span>
                                <input type="email" name="email" required />
                            </label>
                            <label className="field">
                                <span>Message</span>
                                <textarea name="message" rows="5" required></textarea>
                            </label>
                            <button type="submit" className="btn btn-primary">Send message</button>
                        </form>
                    </div>
                </section>
            </main>
        </>
    );
};

export default NewHome;
